/**
 * Desktop MY GAMES — the hub (DESIGN-CHANGELOG 2026-09-03 "MY GAMES: the page
 * about you"; `design/GT Mine.dc.html`, the `isMine` block). Phase 1 of
 * docs/porting/AA.md.
 *
 * A 256px rail beside a main column. The rail is the DOORS: each is a bordered
 * card — a drawn 9×9 glyph in a fixed 19px slot (so every label starts on the
 * same x), the pixel-font label, an optional count, a `▸`. No profile card
 * above them (same-day revision "No profile card in MY GAMES"): you know who you
 * are, and the header's account chip is still the way into PROFILE.
 *
 * The main column is RECOMMENDED TO YOU (phase 3: two-up cards, only what is
 * still waiting on you, hidden when nothing is; `ALL ▸` opens the ledger) over
 * the NOTICE BOARD — the solid bell and a rule, then one row per event, click
 * opens the game. The cards are the one thing here that writes: ADD TO
 * BACKLOG ▸ and NOT FOR ME answer a send, and the card leaves.
 *
 * WHAT IS DELIBERATELY NOT HERE YET. The design draws five doors; phase 1 has
 * two, because BACKLOG, MONITORING and RECOMMENDATIONS are pages the app does
 * not have. A door that opened onto nothing would claim a page the backend
 * cannot serve. Each later phase adds its own, and `mineVM` is where.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { DoorIcon } from '../../components/DoorIcon.tsx';
import { useArt, useQueueArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { mineVM } from '../../store/selectors.ts';
import type { NoticeRow, RecToYouCard } from '../../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

/** `GT_LOGIC.BELL_SOLID` — the board's heading glyph, drawn like the doors'. */
const BELL = 'M3 0h3v1H3zM2 1h5v2H2zM1 3h7v3H1zM0 6h9v1H0zM3 8h3v1H3z';

function NoticeLine({ l }: { l: NoticeRow }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const art = useArt(l.id + 'p', l.coverCands);
  return (
    <div
      onClick={() => openGame(l.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        padding: '15px 0',
        borderBottom: '1px solid var(--gt-line-0)',
        cursor: 'pointer',
      }}
    >
      <span style={{ width: 36, flex: 'none', fontFamily: MONO, fontSize: 11, letterSpacing: '.06em', color: 'var(--gt-tick)' }}>
        {l.w}
      </span>
      <div
        style={{
          position: 'relative',
          width: 45,
          height: 68,
          flex: 'none',
          background: l.fall,
          overflow: 'hidden',
          border: '1px solid var(--gt-line)',
        }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={11} ini={l.ini} hasCover={art.hasCover} cover={art.cover} />
      </div>
      <div style={{ flex: 1, minWidth: 0, maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: 16, color: 'var(--gt-text)' }}>{l.n}</span>
        <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: 14, lineHeight: 1.5, color: 'var(--gt-muted)', textWrap: 'pretty' }}>
          {l.t}
        </span>
      </div>
      <span
        style={{
          fontFamily: MONO,
          fontSize: 9,
          letterSpacing: '.09em',
          color: l.tagClr,
          border: '1px ' + l.tagBs + ' ' + l.tagClr,
          background: l.tagBg,
          padding: '4px 7px',
          flex: 'none',
        }}
      >
        {l.tag}
      </span>
    </div>
  );
}

function RecCard({ c }: { c: RecToYouCard }): JSX.Element {
  const st = useQd();
  const art = useArt(c.gid + 'w', c.hdrCands);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, border: '1px solid var(--gt-border)', background: 'var(--gt-card)', padding: 12 }}>
      <div style={{ display: 'flex', gap: 12 }}>
        <div
          onClick={() => st.openGame(c.gid)}
          style={{ position: 'relative', width: 150, height: 70, flex: 'none', background: c.fall, overflow: 'hidden', border: '1px solid var(--gt-line)', cursor: 'pointer' }}
        >
          <CoverArt style={{ position: 'absolute', inset: 0 }} fs={12} ini={c.ini} hasCover={art.hasCover} cover={art.cover} />
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
            <div
              style={{
                position: 'relative',
                width: 18,
                height: 18,
                flex: 'none',
                background: c.fromHueBg,
                display: 'grid',
                placeItems: 'center',
                fontFamily: PIXEL,
                fontSize: 5.5,
                color: 'var(--gt-ink)',
                overflow: 'hidden',
              }}
            >
              {c.fromIni}
              {c.fromHasAv ? <div style={{ position: 'absolute', inset: 0, background: c.fromAvBg }} /> : null}
            </div>
            <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.07em', color: 'var(--gt-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {c.by}
            </span>
          </div>
          <span onClick={() => st.openGame(c.gid)} style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: 15, color: 'var(--gt-text)', cursor: 'pointer' }}>
            {c.n}
          </span>
          <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: 14, lineHeight: 1.5, color: 'var(--gt-muted)', textWrap: 'pretty' }}>{c.note}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <span
          onClick={() => void st.sendAdd(c.id)}
          style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--acc)', border: '1px solid var(--acc)', background: 'var(--accSoft)', padding: '8px 12px', cursor: 'pointer' }}
        >
          ADD TO BACKLOG ▸
        </span>
        <span
          onClick={() => void st.sendSet(c.id, 'x')}
          className="d-hov-acc-bd"
          style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--gt-text-5)', border: '1px solid var(--gt-border-2)', padding: '8px 12px', cursor: 'pointer' }}
        >
          NOT FOR ME
        </span>
      </div>
    </div>
  );
}

export default function Mine(): JSX.Element {
  const s = useQd();
  const v = mineVM(s);

  // The same art fetch the phone hub runs, for the same reason — see the note
  // in `screens/Mine.tsx`. Recommendation cards and notice-board rows are on no
  // other surface that reaches `/api/steam/items`.
  useQueueArt([...v.recs.map((c) => c.gid), ...v.nb.map((l) => l.id)]);

  return (
    <div data-screen-label="My games" style={{ display: 'flex', alignItems: 'stretch', flex: 1 }}>
      {/* rail — the doors --------------------------------------------------- */}
      <div
        style={{
          width: 256,
          flex: 'none',
          boxSizing: 'border-box',
          borderRight: '1px solid var(--gt-line)',
          padding: '22px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {v.doors.map((d) => (
            <div
              key={d.key}
              onClick={() => s.nav(d.screen)}
              className="d-hov-acc-bd"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: '1px solid var(--gt-border)',
                background: 'var(--gt-card)',
                padding: 12,
                cursor: 'pointer',
              }}
            >
              {/* one icon slot for every door, so the labels line up (same-day
                  revision "The rail doors share one icon slot") */}
              <div style={{ width: 19, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DoorIcon kind={d.icon} size={13} />
              </div>
              <span style={{ flex: 1, fontFamily: PIXEL, fontSize: 8, color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>
                {d.t}
              </span>
              {d.hasN ? (
                <span data-tip={d.tip} style={{ fontFamily: MONO, fontSize: 10, color: 'var(--acc)' }}>
                  {d.n}
                </span>
              ) : null}
              <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--gt-text-5)' }}>▸</span>
            </div>
          ))}
        </div>
      </div>

      {/* main — RECOMMENDED TO YOU, then the notice board ----------------------- */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          boxSizing: 'border-box',
          padding: '22px 28px 26px',
          display: 'flex',
          flexDirection: 'column',
          gap: 22,
        }}
      >
        {v.hasRecs ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontFamily: PIXEL, fontSize: '9.5px', color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>RECOMMENDED TO YOU</span>
              <div style={{ flex: 1 }} />
              <span onClick={() => s.nav('recs')} className="d-hov-acc" style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--gt-text-5)', cursor: 'pointer' }}>
                ALL ▸
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 460px))', gap: 10 }}>
              {v.recs.map((c) => (
                <RecCard key={c.key} c={c} />
              ))}
            </div>
          </div>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <svg viewBox="0 0 9 9" style={{ width: 14, height: 14, display: 'block', flex: 'none', color: 'var(--acc)' }} shapeRendering="crispEdges" aria-hidden="true">
              <path d={BELL} fill="currentColor" />
            </svg>
            <span style={{ fontFamily: PIXEL, fontSize: '9.5px', color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>
              NOTICE BOARD
            </span>
          </div>
          <div style={{ borderTop: '1px solid var(--gt-line)' }}>
            {v.nb.map((l) => (
              <NoticeLine key={l.key} l={l} />
            ))}
            {v.nbEmpty ? (
              <div style={{ padding: '26px 0', fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-muted)' }}>
                NOTHING TO REPORT YET
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
